// The Christmas cards offer to people who already bought a poster (backend/Services/CardOffers.cs).
// Every link into the card editor from the offer carries the surface it sits on as ?offer=. The
// preview remembers it per design on this device, and checkout sends it with that design's card
// line, so the card order records which surface sold it (Order.CardOffer) and the dashboard can
// tell offer-driven card sales apart. PostHog gets cards_offer_shown / cards_offer_clicked with
// the same surface; the mails report "shown" from the backend, and "clicked" when the link lands.

import { capturePostHogEvent } from "@/lib/posthog";

/** Mirrors CardOffers.Surfaces. */
export const CARD_OFFER_SURFACES = ["order-page", "my-designs", "email-shipped", "email-campaign"] as const;
export type CardOfferSurface = (typeof CARD_OFFER_SURFACES)[number];

export function asCardOfferSurface(value: string | null | undefined): CardOfferSurface | null {
  const surface = value?.trim().toLowerCase();
  return (CARD_OFFER_SURFACES as readonly string[]).includes(surface ?? "") ? (surface as CardOfferSurface) : null;
}

const STORAGE_KEY = "vellum:card-offers";

function read(): Record<string, CardOfferSurface> {
  if (typeof window === "undefined") return {};
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).flatMap(([jobId, value]) => {
        const surface = asCardOfferSurface(typeof value === "string" ? value : null);
        return surface ? [[jobId, surface]] : [];
      }),
    );
  } catch {
    return {};
  }
}

/** Remembers that this design's card editor was opened from the offer. The latest surface wins. */
export function rememberCardOffer(jobId: string, surface: CardOfferSurface): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...read(), [jobId.toLowerCase()]: surface }));
  } catch {
    // Storage unavailable: the card order is simply not attributed to the offer.
  }
}

/** The offer surface behind this design's card pack, or undefined when it was not the offer. */
export function cardOfferFor(jobId: string): CardOfferSurface | undefined {
  return read()[jobId.toLowerCase()];
}

/** The card editor on a design, opened from the offer on `surface`. For next-intl's router. */
export function cardOfferHref(jobId: string, surface: CardOfferSurface) {
  return { pathname: "/preview/[id]", params: { id: jobId }, query: { cards: "1", offer: surface } } as const;
}

export function trackCardOfferShown(surface: CardOfferSurface, jobId: string): void {
  capturePostHogEvent("cards_offer_shown", { surface, job_id: jobId });
}

export function trackCardOfferClicked(surface: CardOfferSurface, jobId: string): void {
  capturePostHogEvent("cards_offer_clicked", { surface, job_id: jobId });
}
